import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Loader2, KeyRound } from "lucide-react";
import { startAuthentication } from "@simplewebauthn/browser";
import { toast } from "sonner";
import { BiometricLoginButton, biometricLabel } from "@/components/BiometricSignIn";

export default function Login() {
  const [bioBusy, setBioBusy] = useState(false);
  const { login, mfaVerify, googleSession, setUser, user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [remember, setRemember] = useState(false);
  const [smsMode, setSmsMode] = useState(false);
  const [sms, setSms] = useState({ phone: "", sent: false, code: "" });
  const smsRequest = async () => {
    if (!sms.phone) return toast.error("Enter your mobile number first");
    try { await api.post("/auth/sms/request", { phone: sms.phone }); setSms({ ...sms, sent: true, code: "" }); toast.success("If that number is linked to an account, a code is on its way"); } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Could not send"); }
  };
  const smsVerify = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/auth/sms/verify", { phone: sms.phone, code: sms.code, remember });
      if (data.mfa_required) { setMfaToken(data.mfa_token); toast.message("Staff account — enter your authenticator code"); return; }
      setUser(data.user); toast.success("Signed in"); navigate(roleHome(data.user));
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Invalid code"); }
  };
  const [otpMode, setOtpMode] = useState(false);
  const [otp, setOtp] = useState({ sent: false, code: "" });
  const otpRequest = async () => {
    if (!form.email) return toast.error("Enter your email first");
    try { await api.post("/auth/otp/request", { email: form.email }); setOtp({ sent: true, code: "" }); toast.success("If that account exists, a 6-digit code is on its way"); } catch (e) { toast.error(formatApiError(e)); }
  };
  const otpVerify = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/auth/otp/verify", { email: form.email, code: otp.code, remember });
      if (data.mfa_required) { setMfaToken(data.mfa_token); toast.message("Staff account — enter your authenticator code"); return; }
      setUser(data.user); toast.success("Signed in"); navigate(roleHome(data.user));
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [mfaToken, setMfaToken] = useState("");
  const [mfaCode, setMfaCode] = useState("");

  const [nextUrl] = useState(() => new URLSearchParams(window.location.search).get("next"));
  const roleHome = (u) => nextUrl || (u.role === "admin" ? "/admin" : u.role === "supplier" ? "/supplier" : u.restaurant_slug ? "/restaurant" : u.role === "merchant" ? "/seller" : u.role === "driver" ? "/driver" : "/account");
  useEffect(() => { if (user) navigate(roleHome(user)); }, [user]);

  const passkeyLogin = async () => {
    setError(""); setBioBusy(true);
    try {
      const { data: opts } = await api.post("/webauthn/login/options", { email: form.email || undefined });
      const asseResp = await startAuthentication({ optionsJSON: opts });
      const { data } = await api.post("/webauthn/login/verify", { ...asseResp, remember });
      setUser(data.user);
      navigate(roleHome(data.user));
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(formatApiError(d) || `${biometricLabel()} sign-in was cancelled or isn't set up yet on this device — sign in with your password or an emailed code once, then turn it on when prompted.`);
    }
    setBioBusy(false);
  };

  // Handle Emergent Google redirect (session_id in URL hash)
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes("session_id=")) {
      const sid = new URLSearchParams(hash.slice(1)).get("session_id");
      if (sid) {
        setLoading(true);
        googleSession(sid).then((u) => { window.history.replaceState({}, "", "/login"); navigate(roleHome(u)); })
          .catch(() => { toast.error("Google sign-in failed"); setLoading(false); });
      }
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      const res = await login(form.email, form.password, remember);
      if (res && res.mfa_required) { setMfaToken(res.mfa_token); setLoading(false); return; }
      navigate(roleHome(res));
    }
    catch (err) { setError(formatApiError(err.response?.data?.detail) || "Login failed"); setLoading(false); }
  };

  const verifyMfa = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try { const u = await mfaVerify(mfaToken, mfaCode.trim(), remember); navigate(roleHome(u)); }
    catch (err) { setError(formatApiError(err.response?.data?.detail) || "Invalid code"); setLoading(false); }
  };

  const googleLogin = () => { window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/login")}`; };

  return (
    <div className="min-h-[80vh] grid lg:grid-cols-2">
      <div className="hidden lg:block sn-hero-mesh sn-noise relative">
        <div className="absolute inset-0 p-14 flex flex-col justify-between text-white">
          <div className="font-clash font-bold text-2xl">SHOP NOW</div>
          <div>
            <h2 className="font-clash font-bold text-4xl leading-tight">The Global Marketplace<br />for Everything.</h2>
            <p className="text-white/70 mt-4 max-w-sm">Shop. Book. Discover. Sign in to track orders, earn SHOP Points and unlock member deals.</p>
          </div>
          <div className="text-white/40 text-sm">From Kingston to the World. 🌍</div>
        </div>
      </div>

      <div className="grid place-items-center p-6">
        <div className="w-full max-w-md">
          <h1 className="font-display text-3xl font-bold">Welcome back</h1>
          <p className="text-muted-foreground mt-1">Sign in to your SHOP NOW account.</p>

          {loading && window.location.hash.includes("session_id") ? (
            <div className="grid place-items-center py-16"><Loader2 className="animate-spin text-emerald" size={32} /></div>
          ) : mfaToken ? (
            <form onSubmit={verifyMfa} className="mt-8 space-y-3" data-testid="mfa-step">
              <div className="rounded-xl bg-emerald/10 text-emerald-deep text-sm px-4 py-3">Two-factor is on for this account. Enter the 6-digit code from your authenticator app (or a backup code).</div>
              <input value={mfaCode} onChange={(e) => setMfaCode(e.target.value)} placeholder="Authentication code" inputMode="numeric" autoFocus className="w-full rounded-xl border border-border px-4 py-3 text-sm tracking-widest" data-testid="mfa-code" />
              {error && <p className="text-sm text-coral">{error}</p>}
              <button disabled={loading} className="w-full bg-ink text-white rounded-xl py-3 font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="mfa-verify-btn">
                {loading ? <Loader2 size={18} className="animate-spin" /> : null} Verify &amp; sign in
              </button>
              <button type="button" onClick={() => { setMfaToken(""); setMfaCode(""); setError(""); }} className="w-full text-sm text-muted-foreground" data-testid="mfa-back-btn">Back to login</button>
            </form>
          ) : (
            <>
              <BiometricLoginButton onClick={passkeyLogin} busy={bioBusy} />
              <p className="text-[11px] text-muted-foreground text-center mt-2" data-testid="biometric-hint">Fastest way in. Works once you've turned it on for this device — we'll offer that after your first sign-in.</p>
              <button onClick={googleLogin} className="w-full mt-3 flex items-center justify-center gap-3 border border-border rounded-xl py-3 font-semibold hover:bg-secondary transition-colors" data-testid="google-login-btn">
                <img src="https://www.google.com/favicon.ico" alt="" className="h-5 w-5" /> Continue with Google
              </button>
              <div className="flex items-center gap-3 my-5 text-xs text-muted-foreground"><div className="flex-1 h-px bg-border" /> or <div className="flex-1 h-px bg-border" /></div>
              <label className="flex items-center gap-2 text-sm mb-3 cursor-pointer select-none" data-testid="remember-device-label"><input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="h-4 w-4 accent-emerald" data-testid="remember-device" /> Keep me signed in on this device for 30 days</label>

              {smsMode ? (
              <form onSubmit={smsVerify} className="space-y-3" data-testid="sms-form">
                <input value={sms.phone} onChange={(e) => setSms({ ...sms, phone: e.target.value })} placeholder="Mobile number · 876 555 1234" inputMode="tel" className="w-full rounded-xl border border-border px-4 py-3 text-sm" data-testid="sms-phone" />
                {sms.sent && <input value={sms.code} onChange={(e) => setSms({ ...sms, code: e.target.value.replace(/\D/g, "").slice(0, 6) })} placeholder="6-digit code from the text" inputMode="numeric" className="w-full rounded-xl border border-border px-4 py-3 text-sm tracking-[0.4em] text-center" data-testid="sms-code" />}
                {!sms.sent ? <button type="button" onClick={smsRequest} className="w-full bg-ink text-white rounded-xl py-3 font-semibold" data-testid="sms-send">Text me a code</button>
                  : <button className="w-full bg-ink text-white rounded-xl py-3 font-semibold" data-testid="sms-verify">Verify & sign in</button>}
                <p className="text-[11px] text-muted-foreground">Works for Jamaican mobiles you've linked under Account → Sign in by text message.</p>
                <button type="button" onClick={() => { setSmsMode(false); setSms({ phone: "", sent: false, code: "" }); }} className="w-full text-sm text-muted-foreground" data-testid="sms-back">Back to password sign-in</button>
              </form>
              ) : otpMode ? (
              <form onSubmit={otpVerify} className="space-y-3" data-testid="otp-form">
                <input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="Email" type="email" className="w-full rounded-xl border border-border px-4 py-3 text-sm" data-testid="otp-email" />
                {otp.sent && <input value={otp.code} onChange={(e) => setOtp({ ...otp, code: e.target.value.replace(/\D/g, "").slice(0, 6) })} placeholder="6-digit code from your email" inputMode="numeric" className="w-full rounded-xl border border-border px-4 py-3 text-sm tracking-[0.4em] text-center" data-testid="otp-code" />}
                {!otp.sent ? <button type="button" onClick={otpRequest} className="w-full bg-ink text-white rounded-xl py-3 font-semibold" data-testid="otp-send">Send code</button>
                  : <button className="w-full bg-ink text-white rounded-xl py-3 font-semibold" data-testid="otp-verify">Verify & sign in</button>}
                <button type="button" onClick={() => { setOtpMode(false); setOtp({ sent: false, code: "" }); }} className="w-full text-sm text-muted-foreground" data-testid="otp-back">Back to password sign-in</button>
              </form>
              ) : (
              <form onSubmit={submit} className="space-y-3">
                <input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="Email" type="email" className="w-full rounded-xl border border-border px-4 py-3 text-sm" data-testid="login-email" />
                <input value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} placeholder="Password" type="password" className="w-full rounded-xl border border-border px-4 py-3 text-sm" data-testid="login-password" />
                {error && <p className="text-sm text-coral">{error}</p>}
                <button disabled={loading} className="w-full bg-ink text-white rounded-xl py-3 font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="login-submit">
                  {loading ? <Loader2 size={18} className="animate-spin" /> : null} Sign In
                </button>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                  <Link to="/forgot-password" className="text-emerald font-semibold" data-testid="forgot-password-link">Forgot password?</Link>
                  <button type="button" onClick={() => setOtpMode(true)} className="text-ink font-semibold" data-testid="otp-login-btn">Email me a code</button>
                  <button type="button" onClick={() => setSmsMode(true)} className="text-ink font-semibold" data-testid="sms-login-btn">Text me a code</button>
                  <button type="button" onClick={passkeyLogin} className="text-ink font-semibold flex items-center gap-1.5" data-testid="passkey-login-btn"><KeyRound size={15} /> Security key</button>
                </div>
              </form>
              )}

              <p className="text-sm text-center mt-6 text-muted-foreground">New to SHOP NOW? <Link to="/register" className="text-emerald font-semibold">Create an account</Link></p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
